/**
 * `createApiSubscriptionsSource` — the subscriptions-mgmt-api port over the
 * shared `ApiClient`.
 *
 * Backs the Settings "Billing" tab: reads the caller's current subscription (the
 * billing/entitlement anchor) so the UI can reflect the plan tier, lifecycle
 * status and any per-subscription overrides. The wire is snake_case (the commons
 * `Subscription` model serialized); this adapter maps it to the camelCase summary
 * the UI renders. The canonical `/subscriptions` path is rewritten onto the
 * `.management.` zone (`/management/subscriptions-mgmt-api/v0/...`) by the
 * dashboard `rewritePath`, so region routing / auth / envelope unwrap are
 * inherited.
 *
 * Reads are pinned to the caller's own customer server-side (external callers
 * cannot pass an arbitrary `customer_id`), so no customer id is sent from here.
 */
import type { Plan } from "@authe/fe-commons/plans";
import { type ApiClient } from "@authe/fe-commons/api";
import { walkPages } from "./pagedList";
import { asNullableString, asNumber, asRecord, asString, isRecord, rowsFromWire } from "./wire";

/** Lifecycle status mirrored from the commons `SubscriptionStatus` enum. */
export type SubscriptionStatus =
  | "incomplete"
  | "trialing"
  | "active"
  | "past_due"
  | "paused"
  | "canceled"
  | "expired";

/** Statuses that still grant the plan's entitlements/quota (active-ish). */
const ENTITLED_STATUSES: ReadonlySet<SubscriptionStatus> = new Set<SubscriptionStatus>([
  "trialing",
  "active",
  "past_due",
]);

/**
 * Does a row in this state still grant its plan?
 *
 * A row with NO status does not — `commons.authz.entitlements.is_entitled_subscription`
 * reads a missing status as not entitled, so the authorizer puts that customer
 * on the free tier. Defaulting it to `active` here offered every control the
 * next call refuses.
 */
export function isEntitledStatus(status: SubscriptionStatus | null | undefined): boolean {
  return status != null && ENTITLED_STATUSES.has(status);
}

export interface SubscriptionSummary {
  id: string;
  /**
   * The plan this subscription grants (`free` / `pro` / `enterprise` / custom),
   * or `null` when the row names none. Not defaulted to `free`: the backend's
   * `plan_by_id(None)` is "no such tier", which on an entitled row is UNKNOWN
   * (grants everything), not the cheapest tier.
   */
  planId: string | null;
  /**
   * Lifecycle status, or `null` when the row carries none. Not defaulted to
   * `active`: a missing status is not entitled (see {@link isEntitledStatus}).
   */
  status: SubscriptionStatus | null;
  displayName: string | null;
  /** Seat / unit count for per-seat plans. */
  quantity: number;
  currency: string | null;
  /** Epoch seconds. */
  trialEnd: number | null;
  currentPeriodEnd: number | null;
  canceledAt: number | null;
  /** When true the subscription ends at `currentPeriodEnd` instead of renewing. */
  cancelAtPeriodEnd: boolean;
  /** Sparse overrides layered over the resolved plan (display-only here). */
  entitlementOverrides: Record<string, unknown>;
  quotaOverrides: Record<string, unknown>;
  /**
   * The row's own metadata bag — where a per-customer `load_profile` lives.
   *
   * Distinct from `entitlementOverrides`: that says which FEATURES this
   * customer has, this carries knobs that are not features. Both already
   * existed on the backend model; only the entitlement half was on the wire.
   */
  metadata: Record<string, unknown>;
  /**
   * The tier this row names AS THE PLAN CATALOGUE STORES IT (subscriptions-mgmt-api
   * embeds it). The tiers in `@authe/fe-commons/plans` are only defaults — an
   * operator may have edited this one, or it may be a bespoke tier this build
   * has never heard of. `null` when the API sent none (an older API, or a tier
   * the API does not know either).
   */
  plan?: Plan | null;
  /** Epoch seconds the row was created (used to pick the newest subscription). */
  createdAt: number | null;
}

/** The embedded tier, when the wire carries one that looks like a plan. */
function planFromWire(raw: unknown): Plan | null {
  const plan = asRecord(raw);
  return typeof plan.id === "string" && Array.isArray(plan.entitlements) && Array.isArray(plan.quotas)
    ? (plan as unknown as Plan)
    : null;
}

export interface SubscriptionsSource {
  /**
   * The caller's current subscription — the newest entitled (active-ish) row,
   * falling back to the newest row overall, or `null` when the customer holds
   * none.
   *
   * `null` is an ANSWER: the read succeeded and this customer subscribes to
   * nothing, which is the free tier (`granted_for_subscription(None)`). A read
   * that fails REJECTS instead, so "could not tell" never looks like "free".
   * Every page is read — the choice is made over all of the customer's rows,
   * as the authorizer's `current_subscription` makes it.
   */
  getCurrentSubscription(): Promise<SubscriptionSummary | null>;
}

function subscriptionFromWire(raw: Record<string, unknown>): SubscriptionSummary {
  return {
    id: asString(raw.id),
    // "" is as absent as a missing key: `plan_by_id("")` finds no tier either.
    planId: asNullableString(raw.plan_id) || null,
    status: (asNullableString(raw.status) || null) as SubscriptionStatus | null,
    displayName: asNullableString(raw.display_name),
    quantity: asNumber(raw.quantity) ?? 1,
    currency: asNullableString(raw.currency),
    trialEnd: asNumber(raw.trial_end),
    currentPeriodEnd: asNumber(raw.current_period_end),
    canceledAt: asNumber(raw.canceled_at),
    cancelAtPeriodEnd: Boolean(raw.cancel_at_period_end ?? false),
    entitlementOverrides: asRecord(raw.entitlement_overrides),
    quotaOverrides: asRecord(raw.quota_overrides),
    metadata: asRecord(raw.metadata),
    plan: planFromWire(raw.plan),
    createdAt: asNumber(raw.created_at),
  };
}

/** Newest-first by `createdAt` (missing timestamps sort last). */
function byNewest(a: SubscriptionSummary, b: SubscriptionSummary): number {
  return (b.createdAt ?? 0) - (a.createdAt ?? 0);
}

/**
 * The rows of one `GET /subscriptions` page.
 *
 * The route answers `{subscriptions, count, next_cursor}` (`list_subscriptions`),
 * not `{items}` — and `rowsFromWire` alone read that as NO rows, for every
 * customer. While no rows meant "unknown" that was invisible; now that no rows
 * is the free tier it would downgrade everybody. `{items}` and a bare array are
 * still read, so a route that moves to the shared list shape keeps working.
 */
export function subscriptionRowsOf(payload: unknown): Record<string, unknown>[] {
  if (isRecord(payload) && Array.isArray(payload.subscriptions)) {
    return payload.subscriptions.filter(isRecord);
  }
  return rowsFromWire(payload);
}

/** The subscription that decides the plan — `current_subscription` in the Python. */
export function currentSubscriptionOf(rows: SubscriptionSummary[]): SubscriptionSummary | null {
  const ordered = [...rows].sort(byNewest);
  return ordered.find((s) => isEntitledStatus(s.status)) ?? ordered[0] ?? null;
}

export function createApiSubscriptionsSource(client: ApiClient): SubscriptionsSource {
  return {
    async getCurrentSubscription(): Promise<SubscriptionSummary | null> {
      const rows = await walkPages(client, "/subscriptions", {
        rowsOf: subscriptionRowsOf,
        // Never a cached page: this read is issued once per session (an
        // infinite `staleTime`), so the only re-read is the one a plan refusal
        // asks for — and that one must see the row as it is now.
        cacheTtlMs: 0,
        // A plan picked from some of the rows is a wrong plan, not a short list.
        requireComplete: true,
      });
      return currentSubscriptionOf(rows.map(subscriptionFromWire));
    },
  };
}
